// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';

// TYPES ---------------------------------------------------------------- //

interface PinMarkerProps {
  /** Outer teardrop fill color */
  backgroundColor: string;
  /** Inner teardrop fill color */
  foregroundColor: string;
  /** Optional icon rendered over the pin center */
  icon?: React.ReactNode;
  /** Outer teardrop size in px (square). Default 36. */
  size?: number;
  /** Optional tooltip applied via the title attribute */
  hintText?: string;
  /** If true, applies a brightness bump on hover (used for deselectable pins) */
  hoverBrighter?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Optional extra content anchored to the pin (e.g. a detail bubble) */
  children?: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

/**
 * Shared 2-layer SVG teardrop pin used by map markers across the app.
 * Consumers provide colors and an optional icon; the pin + foreground
 * geometry, positioning, and hover interaction are shared.
 */
const PinMarker = ({
  backgroundColor,
  foregroundColor,
  icon,
  size = 36,
  hintText,
  hoverBrighter,
  onClick,
  children,
}: PinMarkerProps) => {
  const cls = useStyles({ size, hoverBrighter });
  const foregroundSize = size - 4;

  return (
    <div onClick={onClick}>
      <div className={cls.pin} title={hintText}>
        <svg
          className={cls.pinBackground}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 160 220'
          style={{ width: size, height: size }}
        >
          <path
            fill={backgroundColor}
            d='M80,220c-13.23,0-18.13-12.14-20.48-17.98-.42-1.04-.82-2.02-1.16-2.75l-.1-.21c-3.43-7.72-6.62-14.31-11.04-21.35-4.5-7.22-9.65-13.9-15.11-20.97-5.63-7.31-11.46-14.86-16.53-23.08C5.25,116.95,0,98.89,0,80,0,35.89,35.89,0,80,0s80,35.89,80,80c0,18.97-5.29,37.04-15.72,53.71-5.11,8.34-11.04,16.03-16.77,23.46-5.36,6.95-10.42,13.52-14.83,20.55-4.38,6.98-7.48,13.56-11.08,21.44-.31,.69-.68,1.62-1.07,2.62-2.33,5.92-7.17,18.23-20.53,18.23Z'
          />
        </svg>
        <svg
          className={cls.pinForeground}
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 140 200'
          style={{ width: foregroundSize, height: foregroundSize }}
        >
          <path
            fill={foregroundColor}
            d='M70,0C31.3,0,0,31.3,0,70c0,17.4,5,33.7,14.1,48.4,9.5,15.4,22,28.6,31.6,44,4.7,7.5,8.1,14.5,11.7,22.6,2.6,5.5,4.7,15,12.6,15s10-9.5,12.5-15c3.7-8.1,7-15.1,11.7-22.6,9.6-15.3,22.1-28.5,31.6-44,9.2-14.7,14.2-31,14.2-48.4C140,31.3,108.7,0,70,0Zm0,0'
          />
        </svg>
        {icon ? <div className={cls.pinIcon}>{icon}</div> : null}
      </div>
      {children}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = ({ size, hoverBrighter }: { size: number; hoverBrighter?: boolean }) => ({
  pin: css`
    position: relative;
    cursor: pointer;
    transition:
      transform 0.2s ease-in-out,
      filter 0.2s ease-in-out;

    &:hover {
      transform: scale(1.1);
      ${hoverBrighter ? 'filter: brightness(1.2);' : ''}
    }
  `,
  pinBackground: css`
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  `,
  pinForeground: css`
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
  `,
  pinIcon: css`
    position: absolute;
    bottom: ${Math.round(size * 0.33)}px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default PinMarker;
